/**
 * The two "who" controls on the team screen, and the button each one arms.
 *
 * Split out because they are the bulk of the screen's markup and none of its
 * decisions: every value and every disablement arrives as a prop.
 *
 * @module components/teams/TeamPickers
 */

import React from "react";

import { Combobox } from "@/components/ui";
import { FormField } from "@/components/ui/Form";

/** A person or a module, as the combobox wants them. */
export interface PickerOption {
  value: string;
  label: string;
  hint?: string;
}

export interface StaffPickerProps {
  value: string;
  onChange: (value: string) => void;
  options: readonly PickerOption[];
}

/** The field alone: the dialog around it owns the action. */
export function StaffPicker({ value, onChange, options }: StaffPickerProps): React.ReactElement {
  return (
    <div>
      <FormField
        label="Staff member"
        htmlFor="assignment-management-user"
        helpText="Choose an active staff member who is not already on this programme team."
      >
        <Combobox
          id="assignment-management-user"
          value={value}
          onChange={onChange}
          options={[...options]}
          placeholder="Select a staff member"
          emptyMessage="No staff match"
          data-testid="assignment-management-user"
        />
      </FormField>
    </div>
  );
}

export interface ModuleBuilderPickerProps {
  moduleId: string;
  onModuleChange: (value: string) => void;
  modules: readonly PickerOption[];
  /** Why the module picker refuses; undefined is the only way it is offered. */
  moduleRefusal: string | undefined;
  ownerId: string;
  onOwnerChange: (value: string) => void;
  builders: readonly PickerOption[];
  /** Why the builder picker refuses; undefined is the only way it is offered. */
  ownerRefusal: string | undefined;
}

export function ModuleBuilderPicker({
  moduleId,
  onModuleChange,
  modules,
  moduleRefusal,
  ownerId,
  onOwnerChange,
  builders,
  ownerRefusal,
}: ModuleBuilderPickerProps): React.ReactElement {
  return (
    <>
      <FormField
        label="Module"
        htmlFor="assignment-management-module"
        helpText="Select a module to assign or change its module builder."
      >
        <Combobox
          id="assignment-management-module"
          value={moduleId}
          onChange={onModuleChange}
          options={[...modules]}
          placeholder="Select a module"
          emptyMessage="No modules match"
          disabled={moduleRefusal !== undefined}
          disabledReason={moduleRefusal}
          data-testid="assignment-management-module"
        />
      </FormField>
      <FormField
        label="Module builder"
        htmlFor="assignment-management-module-owner"
        helpText="Choose who builds this module. Anyone not yet a Module Builder on this programme is made one."
      >
        <Combobox
          id="assignment-management-module-owner"
          value={ownerId}
          onChange={onOwnerChange}
          options={[...builders]}
          placeholder={moduleId ? "Select a Module Builder" : "Select a module first"}
          emptyMessage="No staff match"
          disabled={ownerRefusal !== undefined}
          disabledReason={ownerRefusal}
          data-testid="assignment-management-module-owner"
        />
      </FormField>
    </>
  );
}
